/** Post-login redirect: remembering where the user was actually heading.
 *
 * Every auth guard bounces an unauthenticated visitor to a login surface. Without carrying the
 * requested URL along, sign-in drops them on a portal root instead of the page they asked for. The
 * guards append {@link AUTH_REDIRECT_PARAM}; the login entry points read it back through
 * {@link Auth_parseRedirectUrl} and use it as the callback URL.
 *
 * Kept free of server-only imports so client login forms can use it too. */

/** Query param carrying the originally requested URL. Named to match the one the bizz login page
 * already accepted before this was generalised. */
export const AUTH_REDIRECT_PARAM = 'redirect-url';

/** Validate an **untrusted** redirect target (it arrives in a URL anyone can craft) down to a
 * same-origin path, or `null` when it isn't one.
 *
 * Rejecting anything that isn't a plain `/path` is what stops this from becoming an open redirect:
 * a login link carrying `?redirect-url=https://evil.example` would otherwise hand an attacker a
 * funkey.be-branded jump to their own page, right after the user typed their password. */
export function Auth_parseRedirectUrl(value: string | null | undefined): `/${string}` | null {
    if (typeof value !== 'string') return null;

    const target = value.trim();

    // Must be a root-relative path. Anything else — absolute URLs, `javascript:`, bare segments —
    // is not ours to redirect to.
    if (!target.startsWith('/')) return null;

    // `//evil.example` and `/\evil.example` are protocol-relative: same-origin by string shape,
    // cross-origin once a browser resolves them.
    if (target.startsWith('//') || target.startsWith('/\\')) return null;

    // Allowlist the characters a URL path/query may contain, rather than blocklisting the dangerous
    // ones. This drops backslashes, whitespace and control characters (the newlines used for header
    // injection among them) — all normalised inconsistently across browsers. Non-ASCII must arrive
    // percent-encoded; when it doesn't we lose the deep link, which is the documented fallback.
    if (!/^\/[\w\-./~%!$&'()*+,;=:@?#[\]]*$/.test(target)) return null;

    return target as `/${string}`;
}

/** Append the requested URL to a login URL, so the login flow can send the user back there.
 * A target that isn't a safe same-origin path is dropped rather than rejected — a bad `redirect-url`
 * should still let the user log in, just without the deep link. */
export function Auth_loginUrlWithRedirect(
    loginUrl: `/${string}`,
    target: string | null | undefined,
): `/${string}` {
    const redirectUrl = Auth_parseRedirectUrl(target);
    if (!redirectUrl) return loginUrl;

    return `${loginUrl}${loginUrl.includes('?') ? '&' : '?'}${AUTH_REDIRECT_PARAM}=${encodeURIComponent(redirectUrl)}`;
}
